import { z } from 'zod';
import { EVAL_TENANT_ID } from '@terra/api-common-constants';
import { EvaluationOutcomeDescriptorSchema } from './evaluation-component.schemas';

export const EvaluationDatasetTypeSchema = z.enum([
  'signal',
  'prompt_instruction',
]);

export const EvaluationDatasetAttackTraceSourceManifestSchema = z.object({
  experimentId: z.string(),
  configName: z.string(),
  coveredSignalCount: z.number().int().nonnegative(),
  missingSignalCount: z.number().int().nonnegative(),
});

export const EvaluationDatasetResourceManifestSchema = z.object({
  applicationCount: z.number(),
  endpointCount: z.number(),
  harEntryCount: z.number(),
  signalCount: z.number(),
  signalAuditLogCount: z.number(),
  credentialCount: z.number().optional().default(0),
  rootType: z.string().optional(),
  collectionCounts: z.record(z.string(), z.number()).optional(),
  attackTraceSource:
    EvaluationDatasetAttackTraceSourceManifestSchema.optional(),
});

export const EvaluationDatasetSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  version: z.number(),
  source: z.string(),
  datasetType: EvaluationDatasetTypeSchema.default('signal'),
  description: z.string().nullable(),
  sourceRef: z.string().nullable(),
  redacted: z.boolean(),
  resourceManifest: EvaluationDatasetResourceManifestSchema.nullable(),
  checksumSha256: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  instantiationCount: z.number(),
});

export const ListEvaluationDatasetsResponseSchema = z.object({
  datasets: z.array(EvaluationDatasetSummarySchema),
  total: z.number(),
});

export const ImportEvaluationDatasetRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
    description: z.string().max(2000).optional(),
    datasetType: EvaluationDatasetTypeSchema.optional().default('signal'),
    source: z.enum(['UPLOAD', 'GIT', 'TENANT_SNAPSHOT']).optional(),
    sourceRef: z.string().max(2000).optional(),
    redacted: z.boolean().optional(),
    bundle: z.unknown().optional(),
    zipBase64: z.string().min(1).optional(),
  })
  .superRefine((value, context) => {
    if (!value.bundle && !value.zipBase64) {
      context.addIssue({
        code: 'custom',
        message: 'Either zipBase64 or bundle is required',
        path: ['zipBase64'],
      });
    }
  });

export const ImportEvaluationDatasetResponseSchema = z.object({
  dataset: EvaluationDatasetSummarySchema,
});

export const CloneEvaluationDatasetAttackTraceSourceSchema = z.object({
  experimentId: z.string().min(1),
  configName: z.string().min(1).max(100),
});

export const CloneEvaluationDatasetRequestSchema = z.object({
  sourceDatasetId: z.string().min(1),
  name: z.string().min(1).max(120),
  attackTraceSource: CloneEvaluationDatasetAttackTraceSourceSchema,
});

export const CloneEvaluationDatasetAttackTraceCoverageSchema = z.object({
  signalCount: z.number().int().nonnegative(),
  coveredSignalCount: z.number().int().nonnegative(),
  missingSignalIds: z.array(z.string()),
  harCoveredSignalCount: z.number().int().nonnegative().optional(),
  signalsMissingHar: z.array(z.string()).optional(),
});

export const CloneEvaluationDatasetResponseSchema = z.object({
  dataset: EvaluationDatasetSummarySchema,
  attackTraceCoverage: CloneEvaluationDatasetAttackTraceCoverageSchema,
});

export const EvalApplicationTeardownSkipReasonSchema = z.enum([
  'in_use',
  'shared',
]);

export const EvalApplicationTeardownSkipSchema = z.object({
  applicationId: z.string(),
  reason: EvalApplicationTeardownSkipReasonSchema,
});

export const DeleteEvaluationDatasetResponseSchema = z.object({
  deleted: z.boolean(),
  skippedTeardowns: z.array(EvalApplicationTeardownSkipSchema),
});

export const BulkDeleteEvaluationDatasetsRequestSchema = z.object({
  datasetIds: z.array(z.string().min(1)).min(1).max(200),
});

export const BulkDeleteEvaluationDatasetsResultItemSchema = z.object({
  datasetId: z.string(),
  deleted: z.boolean(),
  error: z.string().optional(),
  skippedTeardowns: z.array(EvalApplicationTeardownSkipSchema).optional(),
});

export const BulkDeleteEvaluationDatasetsResponseSchema = z.object({
  results: z.array(BulkDeleteEvaluationDatasetsResultItemSchema),
});

export const EvalTenantInventoryApplicationSchema = z.object({
  applicationId: z.string(),
  name: z.string(),
  url: z.string(),
  endpointCount: z.number().int().nonnegative(),
  signalCount: z.number().int().nonnegative(),
  automationOff: z.boolean(),
  instantiationStatus: z.string().nullable(),
  datasetIds: z.array(z.string()),
  datasetNames: z.array(z.string()),
  lastRunAt: z.string().nullable(),
  inUse: z.boolean(),
  orphaned: z.boolean(),
});

export const ListEvalTenantApplicationsResponseSchema = z.object({
  applications: z.array(EvalTenantInventoryApplicationSchema),
});

export const DeleteEvalTenantApplicationRequestSchema = z.object({
  force: z.boolean().optional(),
});

export const DeleteEvalTenantApplicationResponseSchema = z.object({
  deleted: z.boolean(),
  skippedReason: z.string().optional(),
});

export const DisableEvalTenantApplicationAutomationResponseSchema = z.object({
  applicationId: z.string(),
  automationOff: z.boolean(),
});

export const DeleteEvaluationDatasetTasksRequestSchema = z.object({
  signalIds: z.array(z.string().min(1)).min(1).max(500),
});

export const DeleteEvaluationDatasetTasksResponseSchema = z.object({
  dataset: EvaluationDatasetSummarySchema,
  removedSignalIds: z.array(z.string()),
});

export const ReorderEvaluationDatasetTasksRequestSchema = z.object({
  signalIds: z.array(z.string().min(1)).min(1).max(500),
});

export const ReorderEvaluationDatasetTasksResponseSchema = z.object({
  dataset: EvaluationDatasetSummarySchema,
});

export const EvaluationDatasetRootTypeSchema = z.enum([
  'application',
  'externalNetwork',
]);

export const EvaluationEntitySampleSelectorsSchema = z.record(
  z.string().min(1),
  z.array(z.string().min(1)).max(500),
);

export const SnapshotEvaluationDatasetRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
    applicationId: z.string().min(1).optional(),
    rootType: EvaluationDatasetRootTypeSchema.optional(),
    rootIds: z.array(z.string().min(1)).max(50).optional(),
    sampleSelectors: EvaluationEntitySampleSelectorsSchema.optional(),
    tenantId: z.string().min(1).optional(),
    description: z.string().max(2000).optional(),
    datasetType: EvaluationDatasetTypeSchema.optional().default('signal'),
    signalIds: z.array(z.string().min(1)).max(500).optional(),
    redact: z.boolean().optional(),
    componentId: z.string().min(1).optional(),
  })
  .superRefine((value, context) => {
    if (!value.applicationId && (value.rootIds?.length ?? 0) === 0) {
      context.addIssue({
        code: 'custom',
        message: 'Either applicationId or rootIds is required',
        path: ['rootIds'],
      });
    }
  });

export const EvaluationDatasetRootSchema = z.object({
  id: z.string(),
  label: z.string(),
  rootType: EvaluationDatasetRootTypeSchema,
  description: z.string().nullable(),
});

export const ListEvaluationDatasetRootsResponseSchema = z.object({
  roots: z.array(EvaluationDatasetRootSchema),
});

export const SnapshotEvaluationDatasetResponseSchema = z.object({
  dataset: EvaluationDatasetSummarySchema,
  attackTraceCoverage:
    CloneEvaluationDatasetAttackTraceCoverageSchema.optional(),
});

export const InstantiateEvaluationDatasetRequestSchema = z.object({
  datasetId: z.string().min(1),
  tenantId: z.literal(EVAL_TENANT_ID).default(EVAL_TENANT_ID),
  targetUrlOverride: z.url().optional(),
});

export const EvaluationDatasetInstantiationSchema = z.object({
  id: z.string(),
  datasetId: z.string(),
  tenantId: z.string(),
  applicationId: z.string().nullable(),
  status: z.string(),
  targetUrlOverride: z.string().nullable(),
  error: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const InstantiateEvaluationDatasetResponseSchema = z.object({
  instantiation: EvaluationDatasetInstantiationSchema,
});

export const EnsureEvalTenantDatasetInstantiationRequestSchema = z.object({
  datasetId: z.string().min(1),
  targetUrlOverride: z.url().optional(),
});

export const EnsureEvalTenantDatasetInstantiationResponseSchema = z.object({
  results: z.array(
    z.object({
      tenantId: z.literal(EVAL_TENANT_ID),
      skipped: z.boolean(),
      instantiation: EvaluationDatasetInstantiationSchema,
    }),
  ),
});

export const ListEvaluationDatasetInstantiationsQuerySchema = z.object({
  datasetId: z.string().optional(),
  limit: z.coerce.number().min(1).max(500).optional().prefault(500),
  offset: z.coerce.number().min(0).optional().prefault(0),
});

export const ListEvaluationDatasetInstantiationsResponseSchema = z.object({
  instantiations: z.array(EvaluationDatasetInstantiationSchema),
  total: z.number(),
});

export const SuggestEvaluationDataRequestSchema = z
  .object({
    componentId: z.string().min(1),
    tenantId: z.string().min(1),
    rootType: EvaluationDatasetRootTypeSchema.optional(),
    rootId: z.string().min(1).optional(),
    applicationId: z.string().min(1).optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.rootId && !value.applicationId) {
      ctx.addIssue({
        code: 'custom',
        path: ['rootId'],
        message: 'Provide rootId (or applicationId for an application root)',
      });
    }
  });

export const DocumentedVulnerabilityForAutoTagSchema = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  method: z.string().min(1),
  path: z.string().min(1),
  host: z.string().optional(),
  hostIncludes: z.string().optional(),
  signalCategory: z.string().min(1),
  confirmedCategories: z.array(z.string().min(1)).optional(),
  suspectedCategories: z.array(z.string().min(1)).optional(),
});

export const SuggestEvaluationDatasetExpectationsRequestSchema = z.object({
  applicationId: z.string().min(1),
  tenantId: z.string().min(1),
  applicationName: z.string().optional(),
  signalIds: z.array(z.string().min(1)).max(500).optional(),
  vulnerabilities: z
    .array(DocumentedVulnerabilityForAutoTagSchema)
    .min(1)
    .max(200),
});

export const SuggestEvaluationDatasetExpectationsResponseSchema = z.object({
  expectations: z.record(z.string(), z.record(z.string(), z.string())),
  taggedCount: z.number().int().nonnegative(),
});

export const EvaluationDataCandidateSchema = z.object({
  id: z.string(),
  label: z.string(),
  kind: z.string(),
  collection: z.string(),
  metadata: z.record(z.string(), z.unknown()),
});

export const EvaluationRequirementSuggestionSchema = z.object({
  kind: z.string(),
  required: z.boolean(),
  description: z.string(),
  matchCount: z.number(),
  candidates: z.array(EvaluationDataCandidateSchema),
  unavailableReason: z.string().optional(),
});

export const SuggestEvaluationDataResponseSchema = z.object({
  componentId: z.string(),
  engine: z.string(),
  tenantId: z.string(),
  rootType: EvaluationDatasetRootTypeSchema,
  rootId: z.string(),
  applicationId: z.string(),
  requirements: z.array(EvaluationRequirementSuggestionSchema),
});

export const EvaluationDatasetExpectationSampleSchema = z.object({
  signalId: z.string(),
  label: z.string().nullable(),
  category: z.string().nullable(),
  endpointUrl: z.string().nullable(),
  attackThreadId: z.string().nullable().optional(),
  expectations: z.record(z.string(), z.string()),
});

export const EvaluationDatasetExpectationsResponseSchema = z.object({
  datasetId: z.string(),
  datasetName: z.string(),
  componentId: z.string().nullable(),
  outcomeDescriptors: z.array(EvaluationOutcomeDescriptorSchema),
  samples: z.array(EvaluationDatasetExpectationSampleSchema),
  labelledSampleCount: z.number(),
});

export const PatchEvaluationDatasetExpectationsRequestSchema = z.object({
  componentId: z.string().min(1).optional(),
  expectations: z.record(
    z.string(),
    z.record(z.string(), z.string().nullable()),
  ),
});

export type EvaluationDatasetExpectations = z.infer<
  typeof EvaluationDatasetExpectationsResponseSchema
>;
export type EvaluationDatasetExpectationSample = z.infer<
  typeof EvaluationDatasetExpectationSampleSchema
>;
export type EvaluationDatasetType = z.infer<typeof EvaluationDatasetTypeSchema>;

export type EvaluationDatasetInstantiation = z.infer<
  typeof EvaluationDatasetInstantiationSchema
>;

export type EvaluationDatasetSummary = z.infer<
  typeof EvaluationDatasetSummarySchema
>;

export type EvalTenantInventoryApplication = z.infer<
  typeof EvalTenantInventoryApplicationSchema
>;
export type EvalApplicationTeardownSkip = z.infer<
  typeof EvalApplicationTeardownSkipSchema
>;
